import { Injectable } from '@angular/core';
import { OrigamiHttpResponse, OrigamiResponse, ServiceBase } from './base/service.base';
import { Observable } from 'rxjs';
import { UserServiceModel, UserServiceOptions } from './user.service';
import { FolderManagementServiceOptions } from '@app/origami-api/services/folder-management.service';
import {
  InvestigativeSite,
  InvestigativeSiteTopic,
  SimpleTopic,
  TmfPublishing,
  Topic,
  TopicField,
  TopicUtils,
} from '@app/origami-api/utils/topic-utils';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { InvestigativeSite as InvestigativeSiteConstant } from '@app/origami-api/constants/investigative-site';
import { GroupType } from '@app/origami-api/constants/group-types';
import { tap } from 'rxjs/operators';
import { Converter } from '@app/shared/utils/converter';
import { HttpXhrBackend } from '@angular/common/http';
import { CASAuthRequestProxy } from './base/request-proxy/cas-auth.request-proxy.class';
import { TimeZoneService } from '@app/services/time-zone.service';
import { FieldNames } from '../constants';
import { SessionService } from '@app/session/session.service';
import { TopicServiceModel } from '@app/origami-api/models/topic-service.model';

export enum invSiteRetrievalOptions {
  all = 0,
  inDataRoom = 1,
  notInDataRoom = 2,
}

export enum InvSiteFilterMode {
  bySponsorContact = 1,
  byCountry = 2,
  bySite = 3,
  bySiteActivationSpecialist = 4,
  byStatus = 5,
  byIrb = 6,
  byAnyContact = 7,
}

export enum InvestigativeSiteSecurityCheckLevel {
  DomainAndDataRoom = 1,
  DataRoomOnly = 2,
  SkipSecurityCheck = 3,
}

export interface GetContactsViewsParams {
  siteId: number;
  viewFilter: string;
  viewMode: ContactsViewMode;
  searchTextGetter?: () => string;
}

export enum ContactsViewMode {
  ByInvestigativeSite = 1,
  ByCountryByInvestigativeSite = 2,
  ByIRBByInvestigativeSite = 3,
  ByContactType = 4,
  ByInvestigativeSiteTruncated = 5,
}

export interface GetUserInvSiteMemberOfContactType {
  siteId: number;
  investigativeSiteId: number;
  contactTypeCategoryIds: number[];
}

export interface ContactView {
  ViewFilterDisplayName: string;
  ViewFilterId: string;
  EntitiesCount?: number;
  ViewFilterIdAdditional: string;
  ViewMode: number;
}
export interface TruncatedInvestigativeSiteModel {
  InvestigativeSite: TruncatedInvestigativeSite;
  InvestigativeSiteBindedToDataRoom: TruncatedInvestigativeSiteBindedToDataRoom;
}

export interface TruncatedInvestigativeSite {
  Country: string;
  TopicFields: TopicField[];
  TopicId: number;
  TopicName: string;
}

export interface TruncatedInvestigativeSiteBindedToDataRoom {
  InvestigativeSiteName: string;
  PrincipalInvestigator: string;
  SiteActivationDate: string;
  StatusId: number;
  TopicFields: TopicField[];
  TopicId: number;
  SiteNumber?: string;
  TopicName?: string;
}

export namespace InvestigativeSiteServiceModel {
  export interface SimpleContact extends UserServiceModel.User {
    UserId: number;
    GlobalUserTopic?: SimpleTopic;
    UserTopic?: SimpleTopic;
    TopicFields?: TopicField[];
  }

  export interface Contact extends SimpleContact {
    ContactType: any; // should be number but in base interface it is a string
    ContactTypeId: number;
    InvestigativeSiteId: number;
    ContactTypeName: string;
    InvestigativeSiteName: string;
    SiteNumber: string;
    ContactStatus: InvestigativeSiteConstant.ContactStatus;
    IsUserMainContact: boolean;
    ProvideDocuments: boolean;
    InvestigativeSitesCount: number;
    ContactStatusDate?: string;
    ContactStatusComments?: string;
  }

  export interface ContactsInfo {
    Contacts: ContactInfo[];
    IsUserCanAddContact: boolean;
  }

  export interface ContactInfo {
    Address: string | null;
    City: string | null;
    ContactId: number;
    ContactName: string;
    ContactTypeCategoryId: number;
    ContactTypeCategoryName: string;
    ContactTypeId: number;
    ContactTypeName: string;
    ContactStatusId?: number;
    Country: string | null;
    EmailAddress: string;
    FirstName: string | null;
    FullName: string | null;
    GlobalTopicId: number | null;
    InvestigativeSiteId: number;
    InvestigativeSiteName: string;
    InvestigativeSiteStatusId: number;
    IsUserMainContact: boolean;
    IsUserSitesMember: boolean;
    LastName: string;
    LocalTopicId: number | null;
    MobileNo: string | null;
    PhoneNo: string | null;
    ProvideDocuments: boolean;
    UserId?: number;
    RoleLevel: number | null;
    SponsorContactId: number | null;
    State: string | null;
    StatusId: number;
    ZipCode: number | null;
  }

  export interface ContactsViewsResult {
    ContactsList: Contact[];
    TotalRowCount: number;
  }

  export interface InvestigativeSiteView {
    InvSiteFilterMode: InvSiteFilterMode;
    RecordsCount: number;
    ViewFilterDisplayName: string;
    ViewFilterId: string;
  }

  export interface ContactsMembershipInfo {
    ContactId: number;
    ContactName: string;
    EmailAddress: string;
    FirstName: string;
    LastName: string;
    SiteId: number;
    SiteName: string;
    SiteType: string;
    StudyId: string;
  }
}

export enum UseMySitesOnlyFilter {
  AllSites = 0,
  MyCraSasSites = 1,
  MyFavoriteSites = 2,
  MyCraAndFavoriteSites = 3,
}

export enum InvestigativeSiteViewOptions {
  None = 0,
  AddAllAndFavorite = 1,
}

export enum ContactTypeCategoryEnum {
  PrincipalInvestigator = 1,
  SubInvestigator = 2,
  RegulatorContact = 3,
  ContractsContact = 4,
  FinanceBudgetContact = 5,
  LeadStudyCoordinator = 6,
  BackupStudyCoordinator = 7,
  PharmacyContact = 8,
  LaboratoryContact = 9,
  InformationTechnologyContact = 10,
  Other = 11,
  SponsorContact = 12,
  SiteActivationSpecialist = 13,
}

export namespace InvestigativeSiteServiceOptions {
  export interface GetContactsByView {
    siteId: number;
    viewFilter: number | string;
    viewMode: number;
    fieldListing?: string;
    orderBy?: string;
    searchQuery?: string;
    search?: string;
    limit?: number;
    start?: number;
    excludeContactTypes?: ContactTypeCategoryEnum[];
  }

  export interface GetInvestigativeSite {
    siteId: number;
    investigativeSiteTopicId: number;
    domainLevelFields?: string[];
    siteLevelFields?: string[];
    tmfPublishing?: TmfPublishing;
  }

  export interface GetInvestigativeSiteViews {
    siteId: number;
    viewType: InvSiteFilterMode;
    viewFilter?: string | number;
    useMySitesOnlyFilter: UseMySitesOnlyFilter;
    amendmentProfileId?: number;
    options?: InvestigativeSiteViewOptions; // Special field to SSU Country grid
  }

  export interface GetInvestigativeSites {
    siteId: number;
    sponsorId: number;
    options: number;
    domainLevelFields: string[];
    siteLevelFields: string[];
    useMySitesOnlyFilter: UseMySitesOnlyFilter;
    start?: string | number;
    limit?: string | number;
    orderByClause?: string;
    searchClause?: string;
    searchQuery?: string;
    tmfPublishing?: TmfPublishing;
    filterMode?: InvestigativeSiteConstant.FilterMode;
    filterValue?: string;
    securityCheckLevelId?: number;
    amendmentProfileId?: number;
    doNotReturnContacts?: boolean;
    investigativeSiteId?: number;
  }

  export interface GetInvestigativeSitesResult {
    InvSitesList: InvestigativeSite[];
    TotalRowCount: number;
  }

  export interface GetTruncatedInvestigativeSitesResult {
    InvSitesList: TruncatedInvestigativeSiteModel[];
    TotalRowCount: number;
    PageNumber?: number;
  }

  export interface ModifyInvestigativeSite {
    investigativeSite: InvestigativeSiteTopic;
    investigativeSiteInDataRoom: InvestigativeSiteTopic;
    sasCraRemovalValidation?: boolean;
    irbTopics?: SimpleTopic[];
    forceRemoveContacts?: boolean;
  }

  export interface ConvertContactsToUsers {
    aclGroupName: number;
    actionIds: number[];
    isSiteUser: boolean;
    siteId: number;
    userNames: string[];
  }

  export interface AssignInvestigativeSiteAccess {
    assignOption: number;
    filterMode: number;
    filterValue: string;
    groupIds: (number | string)[];
    searchClause?: string;
    securityType: InvestigativeSiteConstant.InvestigativeSiteSecurityType;
    siteId: number;
    useMySitesOnlyFilter?: UseMySitesOnlyFilter;
    userIds: (number | string)[];
  }

  export interface GetContactProfile {
    contactName?: string;
    contactId?: number;
    siteId: number;
    investigativeSiteId?: number;
  }

  export interface ExportInvestigativeSitesData {
    contactsFields: string[];
    exportFormat: number;
    exportType: number;
    filterMode: number;
    filterValue: string;
    searchClause: string;
    siteId: number;
    sitesFields: string[];
    useMySitesOnlyFilter: UseMySitesOnlyFilter;
    investigativeSiteIds?: number[];
  }

  export interface InvSiteMassCoding {
    siteId: number;
    investigativeSites: number[];
    globalFields?: TopicField[];
    localFields?: TopicField[];
    contacts: any[]; // TODO: check type
  }

  export interface GetTruncatedInvestigativeSiteContacts {
    siteId: number;
    invSiteId: number;
    checkIfCanAddNewContact?: boolean;
    tmfPublishing: TmfPublishing;
    securityCheckLevelId?: number;
  }

  export interface DeleteInvestigativeSite {
    siteId: number;
    deleteReason: string;
    deleteFlags: number;
    investigativeSiteTopicIds: number[];
  }

  export interface AddContactsToInvestigativeSites {
    siteId: number;
    contacts: {
      InvestigativeSiteId: number;
      Address: string;
      ContactType: number;
      FirstName: string;
      GlobalUserTopic: Topic;
      LastName: string;
      UserName: string;
      UserTopic: Topic;
      GroupTypes?: GroupType[];
      TopicFields?: TopicField[];
    }[];
  }

  export interface UpdateInvestigativeSiteContactsBody {
    siteId: number;
    investigativeSiteId: number;
    contacts: InvestigativeSiteServiceModel.SimpleContact[];
  }

  export interface DeleteInvestigativeSiteContactsBody {
    siteId: number;
    investigativeSiteIds: number[];
    contactNames: string[];
  }

  export interface GenerateInvestigativeSitePin {
    siteId: number;
    investigativeSiteId: number;
    regeneratePin: 0 | 1;
  }

  export interface GetInvestigativeSiteContacts {
    siteId: number;
    invSiteIdsFilter: string;
    tmfPublishing?: TmfPublishing;
  }

  export interface GetContactInvestigativeSites {
    siteId: number;
    contactId: number;
  }

  export interface GetContactsMembershipInfo {
    siteId: number;
    contactNames: string[];
  }

  export interface ChangeContactNames {
    siteId: number;
    nameChangeRequests: {
      ContactName: string;
      FirstName: string;
      LastName: string;
      SelectedSites: number[];
    }[];
  }

  export interface EditContactOptions {
    siteId: number;
    // Investigative-site scope for the edit — siteId above is only the room id, so the backend
    // needs this to know which site's contact link is being edited. Optional: not every
    // EditContact caller is scoped to a single investigative site (e.g. contact-level coding).
    investigativeSiteId?: number;
    contact: UserServiceOptions.User;
    forceDelete?: boolean;
  }

  export interface CreateUpdateVisitLogRecord {
    siteId: number;
    visitLog: Topic;
  }

  export interface DeleteVisitLogRecords {
    siteId: number;
    visitLogIds: number[];
  }

  export enum BudgetContractType {
    Budget = 1,
    Contract = 2,
  }

  export interface GetBudgetsOrContractsOptions {
    siteId: number;
    investigativeSiteId?: number;
    entityType: BudgetContractType;
    start?: number;
    limit?: number;
    orderBy: string;
    searchQuery: string;
  }

  export interface GetBudgetsOrContractsResult {
    PageNumber: number;
    Records: Topic[];
    TotalRowCount: number;
  }

  export interface CreateBudgetOrContractOptions {
    siteId: number;
    entity: Topic;
    fileToken: string;
    investigativeSiteId: number;
    entityType: BudgetContractType;
  }

  export interface ModifyBudgetOrContractOptions {
    siteId: number;
    entity: Topic;
    fileToken: string;
    deleteFile: boolean;
  }

  export interface DeleteBudgetsOrContractsOptions {
    siteId: number;
    entityIds: number[];
  }

  export interface ChangeInvestigativeSiteStatus {
    siteId: number;
    investigativeSiteId: number;
    siteFields: TopicField[];
    status: InvestigativeSiteConstant.Status;
    issues?: any[];
    documentIdsMovingToEtmf: number[];
    doNotMoveAnyDocumentsToEtmf: boolean;
    completeAmendments: boolean;
  }
}

export interface BulkImportInvestigativeSitesResult {
  ErrorRecords: any[];
  ErrorRecordsFilePath: string | null;
  ErrorRecordsTotalCount: number;
  FilePath: string | null;
  JobId: string;
  Metadata: { Metadata: string; WorkSheetName: string }[];
}

export interface IContactsByView {
  limit: number;
  search: string;
  siteId: number;
  start: number;
  viewFilter: string | null;
  viewMode: number;
}

@Injectable({
  providedIn: 'root',
})
export class InvestigativeSiteService extends ServiceBase {
  serviceUrl = 'InvestigativeSiteService.svc';
  invSiteId: number;

  constructor(
    backend: HttpXhrBackend,
    requestProxy: CASAuthRequestProxy,
    private timeZoneService: TimeZoneService,
    private sessionService: SessionService,
  ) {
    super(backend, requestProxy);
  }

  getInvestigativeSite(
    body: InvestigativeSiteServiceOptions.GetInvestigativeSite,
    isForceRequest: boolean = true,
  ): Observable<OrigamiResponse<InvestigativeSite>> {
    body.domainLevelFields = body.domainLevelFields || ['*'];
    body.siteLevelFields = body.siteLevelFields || ['*'];

    return this.origamiPost('/GetInvestigativeSite', body, null, null, isForceRequest).pipe(
      tap((res) => {
        const site: InvestigativeSite = res.Result;

        const createdDate = TopicUtils.getTopicFieldValue(
          site.InvestigativeSiteBindedToDataRoom?.TopicFields,
          FieldNames.Site.CreatedDate,
        );
        site.CreatedDateTime = this.formatDate(createdDate);

        const lastUpdatedDate = TopicUtils.getTopicFieldValue(
          site.InvestigativeSiteBindedToDataRoom?.TopicFields,
          FieldNames.Site.LastUpdatedDate,
        );
        site.LastUpdatedDateTime = this.formatDate(lastUpdatedDate);
      }),
    );
  }

  private formatDate(dateString: string): string {
    if (!dateString) {
      return null;
    }
    const date = Converter.DateAndTime.parseStringToJSDate(
      dateString,
      this.sessionService.domain?.timeZone.IanaZoneName,
    );
    return this.timeZoneService.formatDate(date, null, true);
  }

  getInvestigativeSites(
    body: InvestigativeSiteServiceOptions.GetInvestigativeSites,
    ...params
  ): Observable<OrigamiResponse<InvestigativeSiteServiceOptions.GetInvestigativeSitesResult>> {
    const forceRequest = params.length && params[0]?.forceRequest;

    return this.origamiPost('/GetInvestigativeSites', body, ...params, [], forceRequest);
  }

  getTruncatedInvestigativeSites(
    body: InvestigativeSiteServiceOptions.GetInvestigativeSites,
    ...params
  ): Observable<OrigamiResponse<InvestigativeSiteServiceOptions.GetTruncatedInvestigativeSitesResult>> {
    return this.origamiPost('/GetTruncatedInvestigativeSites', body, ...params);
  }

  getInvestigativeSiteViews(
    body: InvestigativeSiteServiceOptions.GetInvestigativeSiteViews,
    ...params
  ): Observable<OrigamiResponse<InvestigativeSiteServiceModel.InvestigativeSiteView[]>> {
    return this.origamiPost('/GetInvestigativeSiteViews', body, ...params);
  }

  getContactsViews(params: GetContactsViewsParams, ...options): Observable<OrigamiResponse<ContactView[]>> {
    return this.origamiPost('/GetContactsViews', params, ...options);
  }

  getUserInvSiteMemberOfContactType(params: GetUserInvSiteMemberOfContactType, ...options) {
    return this.origamiPost('/IsUserInvSiteMemberOfContactType', params, ...options);
  }

  getContactsByView(
    body: InvestigativeSiteServiceOptions.GetContactsByView,
  ): Observable<OrigamiResponse<InvestigativeSiteServiceModel.ContactsViewsResult>> {
    return this.origamiPost('/GetContactsByView', body, null, null, true);
  }

  getContact(body) {
    return this.origamiPost('/GetContact', body, null, null, true);
  }

  bulkImportInvestigativeSites(
    body: FolderManagementServiceOptions.BulkImportItemsFromMetadata,
  ): Observable<OrigamiResponse<BulkImportInvestigativeSitesResult>> {
    return this.origamiPost('/BulkImportInvestigativeSites', body, null, null, true);
  }

  modifyInvestigativeSite(
    body: InvestigativeSiteServiceOptions.ModifyInvestigativeSite,
  ): Observable<OrigamiHttpResponse> {
    const silentErrorCodes = [ErrorCode.FINISHED_WITH_MINOR_ERRORS];

    return this.origamiPost('/ModifyInvestigativeSite', body, null, silentErrorCodes, true);
  }

  createInvestigativeSite(
    body: InvestigativeSiteServiceOptions.ModifyInvestigativeSite,
    options?,
    silentErrorCodes?: ErrorCode[],
    forceRequest?,
  ) {
    return this.origamiPost('/CreateInvestigativeSite', body, options, silentErrorCodes, forceRequest);
  }

  convertContactsToUsers(
    body: InvestigativeSiteServiceOptions.ConvertContactsToUsers,
  ): Observable<OrigamiHttpResponse> {
    return this.origamiPost('/ConvertContactsToUsers', body, null, null, true);
  }

  assignInvestigativeSiteAccess(body: InvestigativeSiteServiceOptions.AssignInvestigativeSiteAccess) {
    const silentErrorCodes = [ErrorCode.FINISHED_WITH_MINOR_ERRORS];
    return this.origamiPost('/AssignInvestigativeSiteAccess', body, null, silentErrorCodes, true);
  }

  getContactProfile(
    params: InvestigativeSiteServiceOptions.GetContactProfile,
    options?,
    silentErrorCodes?: ErrorCode[],
    forceRequest?,
  ): Observable<OrigamiResponse<InvestigativeSiteServiceModel.Contact>> {
    return this.origamiPost('/GetContactProfile', params, options, silentErrorCodes, forceRequest);
  }

  exportInvestigativeSitesData(body: InvestigativeSiteServiceOptions.ExportInvestigativeSitesData) {
    return this.origamiPost('/ExportInvestigativeSitesData', body, null, null, true);
  }

  invSiteMassCoding(body: InvestigativeSiteServiceOptions.InvSiteMassCoding) {
    return this.origamiPost('/InvSiteMassCoding', body, null, null, true);
  }

  getTruncatedInvestigativeSiteContacts(
    body: InvestigativeSiteServiceOptions.GetTruncatedInvestigativeSiteContacts,
  ): Observable<OrigamiResponse<InvestigativeSiteServiceModel.ContactsInfo>> {
    return this.origamiPost('/GetTruncatedInvestigativeSiteContacts', body, null, null, true);
  }

  deleteInvestigativeSite(params: InvestigativeSiteServiceOptions.DeleteInvestigativeSite) {
    return this.origamiPost('/DeleteInvestigativeSite', params);
  }

  addContactsToInvestigativeSites(
    params: InvestigativeSiteServiceOptions.AddContactsToInvestigativeSites,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<TopicServiceModel.TopicError[]>> {
    return this.origamiPost('/AddContactsToInvestigativeSiteS', params, null, silentErrorCodes, true);
  }

  updateInvestigativeSiteContacts(
    body: InvestigativeSiteServiceOptions.UpdateInvestigativeSiteContactsBody,
  ): Observable<OrigamiHttpResponse> {
    const silentErrorCodes = [ErrorCode.FINISHED_WITH_MINOR_ERRORS];

    return this.origamiPost('/UpdateInvestigativeSiteContacts', body, null, silentErrorCodes, true);
  }

  deleteInvestigativeSiteContacts(
    body: InvestigativeSiteServiceOptions.DeleteInvestigativeSiteContactsBody,
  ): Observable<OrigamiHttpResponse> {
    const silentErrorCodes = [ErrorCode.FINISHED_WITH_MINOR_ERRORS];

    return this.origamiPost('/DeleteInvestigativeSiteContacts', body, null, silentErrorCodes, true);
  }

  generateInvestigativeSitePin(
    params: InvestigativeSiteServiceOptions.GenerateInvestigativeSitePin,
  ): Observable<OrigamiResponse<string>> {
    return this.origamiPost('/GenerateInvestigativeSitePin', params, null, null, true);
  }

  getInvestigativeSiteContacts(
    options?: InvestigativeSiteServiceOptions.GetInvestigativeSiteContacts,
    isForceRequest: boolean = false,
    ...params
  ): Observable<OrigamiResponse<InvestigativeSiteServiceModel.ContactInfo[]>> {
    return this.origamiPost('/GetInvestigativeSiteContacts', options, null, null, isForceRequest);
  }

  getContactsMembershipInfo(
    options?: InvestigativeSiteServiceOptions.GetContactsMembershipInfo,
    isForceRequest: boolean = true,
  ): Observable<OrigamiResponse<InvestigativeSiteServiceModel.ContactsMembershipInfo[]>> {
    return this.origamiPost('/GetContactsMembershipInfo', options, null, null, isForceRequest);
  }

  changeContactNames(
    options?: InvestigativeSiteServiceOptions.ChangeContactNames,
  ): Observable<OrigamiResponse<string>> {
    return this.origamiPost('/ChangeContactNames', options, null, null, true);
  }

  editContact(
    options: InvestigativeSiteServiceOptions.EditContactOptions,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<any[]>> {
    return this.origamiPost('/EditContact', options, null, silentErrorCodes, true);
  }

  createVisitLogRecord(
    params: InvestigativeSiteServiceOptions.CreateUpdateVisitLogRecord,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<string>> {
    return this.origamiPost('/CreateVisitLogRecord', params, null, silentErrorCodes, true);
  }

  updateVisitLogRecord(
    params: InvestigativeSiteServiceOptions.CreateUpdateVisitLogRecord,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<string>> {
    return this.origamiPost('/UpdateVisitLogRecord', params, null, silentErrorCodes, true);
  }

  deleteVisitLogRecords(
    params: InvestigativeSiteServiceOptions.DeleteVisitLogRecords,
    silentErrorCodes?: ErrorCode[],
  ): Observable<OrigamiResponse<string>> {
    return this.origamiPost('/DeleteVisitLogRecords', params, null, silentErrorCodes, true);
  }

  getBudgetsOrContracts(
    params: InvestigativeSiteServiceOptions.GetBudgetsOrContractsOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<InvestigativeSiteServiceOptions.GetBudgetsOrContractsResult>> {
    return this.origamiPost('/GetBudgetsOrContracts', params, null, silentErrorCodes, forceRequest);
  }

  // TODO: change return value when will be using this api
  createBudgetOrContract(
    params: InvestigativeSiteServiceOptions.CreateBudgetOrContractOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/CreateBudgetOrContract', params, null, silentErrorCodes, forceRequest);
  }

  modifyBudgetOrContract(
    params: InvestigativeSiteServiceOptions.ModifyBudgetOrContractOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ModifyBudgetOrContract', params, null, silentErrorCodes, forceRequest);
  }

  deleteBudgetsOrContracts(
    params: InvestigativeSiteServiceOptions.DeleteBudgetsOrContractsOptions,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/DeleteBudgetsOrContracts', params, null, silentErrorCodes, forceRequest);
  }

  changeInvestigativeSiteStatus(
    params: InvestigativeSiteServiceOptions.ChangeInvestigativeSiteStatus,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ChangeInvestigativeSiteStatus', params, null, silentErrorCodes, forceRequest);
  }

  getContactsByViewCached(
    body: InvestigativeSiteServiceOptions.GetContactsByView,
  ): Observable<OrigamiResponse<InvestigativeSiteServiceModel.ContactsViewsResult>> {
    return this.origamiPost('/GetContactsByView', body, null, null, false);
  }
}
